import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Divider, Popover, Typography, Button, IconButton, Modal, CircularProgress, Stack } from '@mui/material';
import _ from 'lodash';
import { getStatusText, getStatusColor, getDisplayName, IsAgileExecutive, timeZoneWiseTime, timeZoneAbbreviation, timeZonWiseDateTime } from '../../components/common';
import ChatAvatarComponent from '../chat/ChatAvatarComponent';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StarIcon from '@mui/icons-material/Star';
import { DeleteIcon } from '../../../../utils/Icons';
import { API } from '../../../../api';
import { toast } from 'react-toastify';
import WelcomeModals from './WelcomeModals';
import ObjectiveModal from './ObjectiveModal';
import { useAuth0 } from '@auth0/auth0-react';
import UserPeersPopup from './UserPeersPopup';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import MatchCancelModal from './MatchCancelModal';
import { navigateToChat } from '../NavigateToChat';
import CallBookingModal from '../CallBookingModal';
import { useModule } from '../../../../utils/useModule';
import { Bone } from '../../../../components/ui/DashboardSkeletons';
import { getUserDetails } from '../../../../utils/orgName';
import { openMeetingWithRedirect } from '../../../../utils/openMeetingWithRedirect';
import useGroupDetails from '../../../../queries/useGroupDetails';
import useLearningObjs from '../../../../queries/useLearningObjs';
import useProfileDetails from '../../../../hooks/useProfileDetails';
import { getAcsUserByCommunicationId } from '../../../../routes/helpers';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { taskActions } from '../../../../redux/slices/goals-tasks/getTasksSlice';
import { RequestMatch, RequestMatchReject } from '../Matches/components/RequestAndRejectMatch';
import { eventTracking, events } from '../../../../utils/amplitude';
import RecruitNewMentor from './RecruitNewMentor';
import UserProfileDetailsPopup from '../../components/UserProfileDetailsPopup';
import { PlusOutlined } from '@ant-design/icons';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DosenEllipsisMenu from './MentorDash/Components/DosenElipsisMenu';
import MenteeProfileModal from './MentorDash/Components/MenteeProfileModal';
import ForumIcon from '@mui/icons-material/Forum';
import { useMemberWord } from '../../../../utils/memberTypeLabels';

const calculateMenteeStats = (mentee: any) => {
  if (!mentee) {
    return {
      messagesShared: 0,
      meetingHistory: 0,
      futureMeetings: 0
    };
  }
  const meetings = Array.isArray(mentee?.meetings) ? mentee.meetings : [];
  let pastMeetings = 0;
  let futureMeetings = 0;
  const now = Date.now();
  meetings.forEach((meeting: any) => {
    const callDetails = meeting?.callDetails || meeting;
    const callDate = callDetails?.callDate ? new Date(callDetails.callDate).getTime() : null;
    if (!callDate || Number.isNaN(callDate)) {
      return;
    }
    if (callDate >= now) {
      futureMeetings += 1;
    } else {
      pastMeetings += 1;
    }
  });
  const messagesShared =
    mentee?.messagesShared ??
    mentee?.totalMessages ??
    mentee?.messagesCount ??
    mentee?.messageCount ??
    (Array.isArray(mentee?.messages) ? mentee.messages.length : 0) ??
    0;
  return {
    messagesShared: messagesShared || 0,
    meetingHistory: pastMeetings,
    futureMeetings
  };
};

const Mentor = () => {
  /** The Agile Executive checking */
  const isAgileExec = IsAgileExecutive();
  const w = useMemberWord();
  // const isStarVentureExec = IsStarVenture();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAuth0();
  const { location, orgId } = getUserDetails();
  const { isMasterclass, isMentorship, isHybrid } = useModule(); // Fetching current module from Redux state
  // const orgDetails = useAppSelector((state) => state.orgDetails);
  // const allGroupsTasks = useAllGroupTasks();
  // const taskData = allGroupsTasks.data ? _.last(allGroupsTasks.data) : [];
  // const groupIds = _.get(taskData, '_id');
  // const levels = _.get(taskData, 'levels');
  // const levelId = _.get(_.head(levels), '_id');
  // const tasks = _.get(_.head(levels), 'tasks');
  // const taskId = _.get(_.head(tasks), '_id');
  const [loadingMentor, setLoadingMentor] = useState('');
  const [peersList, setPeersList] = useState<any>();
  const [connectionCount, setConnectionCount] = useState('');
  const [myMenteesList, setMyMenteesList] = useState<any>([]);
  const [myRequestingMenteesList, setRequestingMenteesList] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [cardsStatsReady, setCardsStatsReady] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [matchProfilesById, setMatchProfilesById] = useState<Record<string, any>>({});
  const [activeMeetingModal, setActiveMeetingModal] = useState<{
    type: 'history' | 'future' | null;
    mentee: any;
    objectiveId: string | null;
  }>({ type: null, mentee: null, objectiveId: null });
  const [meetingHistoryFromApi, setMeetingHistoryFromApi] = useState<{
    past: any[];
    upcoming: any[];
    canceled: any[];
  } | null>(null);
  const [meetingHistoryLoading, setMeetingHistoryLoading] = useState(false);
  const [userLearningObjectives, setUserLearningObjectives] = useState<any[]>([]);
  const avatars = peersList?.map((each: any) => ({ avatar: each?.headshot, firstName: each?.displayName }));
  const [displayName, setDisplayName] = useState('');
  const [participantUser, setParticipantUser] = useState<any>([]);
  const [mentorshipHoursValue, setMentorshipHours] = useState('00:00');
  const [mentorRoi, setMentorRoi] = useState<any>(0);
  const personalThreadsArr = useAppSelector((state) => state.acsChannels.personalThreadsArr);
  const selectedGroupId = useAppSelector((state) => state.selectedGroup.data);
  const communityMembers = useAppSelector((state) => state.acsCommunityUsers.data);
  const commList = useAppSelector((state) => state.getCommunityMembers.data);
  /** Show goal progress */
  const [hasShowGoalProgressValue, setHasShowGoalProgressValue] = useState<any>();
  const groupId = selectedGroupId.id || '';
  const groupList = useAppSelector((state) => state.groupRoleList);
  const [isPeersPopupOpen, setPeersPopupOpen] = React.useState(false);
  /** For responsive */
  const [openCalendarModal, setOpenCalendarModal] = useState(false);
  const [bookedCallDetails, setBookedCallDetails] = useState<any>('');

  const [recruitModalOpen, setRecruitModalOpen] = useState(false);
  const [recruitMentors, setRecruitMentors] = useState<any[]>([]);
  const [selectedPopoverMentee, setSelectedPopoverMentee] = useState<any>(null);
  const [recruitMentorsLoading, setRecruitMentorsLoading] = useState(false);
  const [recruitObjId, setRecruitObjId] = useState('');
  const [recruitObjectiveName, setRecruitObjectiveName] = useState('');
  const [expandedObjectives, setExpandedObjectives] = useState<Record<string, boolean>>({});
  const [openUserProfileDetails, setOpenUserProfileDetails] = React.useState(false);
  const [selectedMenteeInfo, setSelectedMenteeInfo] = useState(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileMentee, setProfileMentee] = useState<any>(null);
  const handleopenUserProfileDetails = (flag: boolean) => setOpenUserProfileDetails(flag);

  const {
    data: menteeList,
    isLoading: myMenteeLoading,
    error,
    isFetching,
    refetch
  } = useQuery({
    queryKey: ['getMentorsPeersHomePage'],
    queryFn: () => {
      if (!groupId) {
        return;
      }
      return API.getMyMentees({ orgId, groupId });
    },
    staleTime: 10 * 10 * 1000
  });
  const { userId: objUserId, gettingStarted, timezone } = useProfileDetails();

  /** Welcome Modals */

  const [openGettingStartedModal, setOpenGettingStartedModal] = useState(false);
  const handleClickWelcomeModals = (flag: boolean) => {
    setOpenGettingStartedModal(flag);
  };
  useEffect(() => {
    if (!gettingStarted) {
      setOpenGettingStartedModal(true);
    }
  }, [gettingStarted]);

  /** For request and reject match dialog */
  const [openRequestMatch, setOpenRequestMatch] = useState(false);
  const [openRequestMatchReject, setOpenRequestMatchReject] = useState(false);
  const [isObjectiveModalOpen, setObjectiveModalOpen] = useState(false);
  const [requestId, setRequestId] = useState('');
  const [mentorId, setMentorId] = useState('');
  const [menteeId, setMenteeId] = useState('');
  const [commId, setCommId] = useState('');
  const [actionId, setActionId] = useState('');
  const [reloadFlag, setReloadFlag] = useState(false);
  const handleRequestMatch = (flag: boolean) => setOpenRequestMatch(flag);
  const handleRequestMatchReject = (flag: boolean) => setOpenRequestMatchReject(flag);
  const [objective, setObjective] = useState<any>({});
  const [selectedMentee, setSelectedMentee] = useState();
  const [matchedObjectives, setMatchedObjectives] = useState([]);
  const [activeExpertiseIndex, setActiveExpertiseIndex] = useState(0);
  const [activeMenteeIndex, setActiveMenteeIndex] = useState(0);
  const menteeCardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const menteeSliderScrollRef = useRef<HTMLDivElement | null>(null);
  const MENTEE_CARD_WIDTH = 304;
  const MENTEE_CARD_HEIGHT = 281;
  const MENTEE_CARD_GAP = 20;
  const handleCloseObjectiveModal = () => {
    setObjectiveModalOpen(false);
    getObjectiveMentees({ orgId, location, groupId });
  };
  /** Popover */
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMatchCancel, setOpenMatchCancel] = useState(false);
  const [selectedMatchId, setSelectedMatchId] = useState<string>('');
  const [recentlyScheduledMeeting, setRecentlyScheduledMeeting] = useState(null);
  const handleOpenMatchCancel = (matchId: string) => {
    setSelectedMatchId(matchId);
    setOpenMatchCancel(true);
    setAnchorEl(null); // Close popover when modal opens
  };
  const handleCloseMatchCancel = async () => {
    setOpenMatchCancel(false);

    if (isMentorship) {
      await getObjectiveMentees({ orgId, location, groupId });
    } else {
      await getMyMenteesList({ orgId, groupId });
    }
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const openMatchesTablePopover = Boolean(anchorEl);
  const idMatchesTablePopover = openMatchesTablePopover ? 'matches-popover' : undefined;
  /** Handle User hotes popup */
  // const [openUserNotes, setOpenUserNotes] = useState(false);
  // const handleOpenUserNotes = (flag: boolean) => setOpenUserNotes(flag);

  // const [groupId, setGroupId] = useState('');
  // const userLastseen = localStorage.getItem('lastseen');
  // const lastSeenRole = userLastseen ? JSON.parse(userLastseen) : null;

  //   useEffect(() => {
  //     dispatch(updateSelectedId(lastSeenRole));
  //   }, []);

  //   useEffect(() => {
  //     if (selectedGroupId) {
  //       setGroupId(selectedGroupId?.id);
  //     }
  //   }, [selectedGroupId]);

  const rejectMatchRequest = (requestId: string, mentorId: string, menteeId: string, commId: string) => {
    setRequestId(requestId);
    setMentorId(mentorId);
    setMenteeId(menteeId);
    setCommId(commId);
    handleRequestMatchReject(true);
  };

  const doReload = () => {
    setLoading(false);
    setReloadFlag(true);
  };

  useEffect(() => {
    if (reloadFlag) {
      setReloadFlag(false);
    }
  }, [reloadFlag]);
  const [requestLoading, setRequestLoading] = useState(false);

  const acceptMatchRequest = async (requestId: string, mentorId: string, menteeId: string) => {
    const grpId = groupId;
    setActionId(requestId);
    setLoading(true);
    setRequestLoading(true);
    try {
      const response = await API.acceptMatchRequest({ grpId, mentorId, menteeId, requestId });
      if (response.status === 200) {
        eventTracking(events.matchByRequest, {});
        await refetch();
      }
    } catch (e) {
    } finally {
      setActionId('');
      setLoading(false);
      setRequestLoading(true);
    }
  };

  const removeMatchRequest = async (requestId: string) => {
    const grpId = groupId;
    setLoading(true);
    setRequestLoading(true);
    try {
      const response = await API.removeMatchRequest({ grpId, requestId });
      if (response.status === 200) {
        await refetch();
      }
    } catch (e) {
    } finally {
      setRequestLoading(false);
      setLoading(false);
    }
  };

  const handlePeersPopup = (open: boolean) => {
    setPeersPopupOpen(open);
  };

  const navigateMember = (member: any) => {
    setSelectedMember(member);
  };

  const handleCalendarOpen = (member: any) => {
    setDisplayName(member.displayName);
    setParticipantUser([member]);
    if (isMentorship) {
      setObjective(member.matchedObjectives[0]);
    }
    setRecentlyScheduledMeeting(null);
    setBookedCallDetails('');
    setOpenCalendarModal(true);
  };

  const handleMeetingScheduled = (meetingDetails: any) => {
    setRecentlyScheduledMeeting(meetingDetails);
    fetchGroupDetails();
    if (isMentorship) {
      getObjectiveMentees({ orgId, location, groupId });
    } else {
      getMyMenteesList({ orgId, groupId });
    }
  };

  const handleCalendarClose = () => {
    setDisplayName('');
    setParticipantUser([]);
    setBookedCallDetails('');
    setOpenCalendarModal(false);
  };

  const moveToUsercommunityProfile = (userId: string) => {
    const mem = communityMembers.find((item: { userId: string }) => item.userId === userId);
    const modifiedMem = { ...mem, id: _.get(mem, 'id.coId', '') };
    const allCommunityMembersList = communityMembers;

    if (mem) {
      navigate('/app/communitymember', {
        state: { mem: modifiedMem, allCommunityMembersList: commList }
      });
    }
  };

  const getMentorsPeersHomePage = async (orgId: string, location: string, groupId: string) => {
    try {
      const response = await API.getMentorsPeersHomePage(orgId, location, groupId);
      if (response.status === 200) {
        setPeersList(response?.data?.data);
        setConnectionCount(response?.data?.connections);
        setLoading(false);
      } else {
        setLoading(true);
      }
    } catch (e) {
      setLoading(true);
    }
  };

  useEffect(() => {
    if ((menteeList?.data?.data?.length > 0 || menteeList) && !isMentorship) {
      //setMyMenteesList(menteeList?.data?.data);
      setRequestingMenteesList(menteeList?.data?.reqestedMentees);
      setHasShowGoalProgressValue(menteeList?.data?.showGoalProgess);
    }
  }, [menteeList]);

  const getMyMenteesList = async ({ orgId, groupId }: { orgId: string; groupId: string }) => {
    try {
      const response = await API.getMyMentees({ orgId, groupId });
      if (response.status === 200) {
        setMyMenteesList(response?.data?.data);
        setRequestingMenteesList(response?.data?.reqestedMentees);
        setHasShowGoalProgressValue(response?.data?.showGoalProgess);
      } else {
        // toast.error('Mantors list failed to load');
      }
    } catch (e) {
      // toast.error('Mantors list failed to load');
    }
  };

  useEffect(() => {
    const mentees = Array.isArray(myMenteesList) ? myMenteesList : [];

    if (mentees.length === 0) {
      setCardsStatsReady(true);
      return;
    }
    if (!orgId || !groupId) return;

    // Every match profile now arrives in a single consolidated BE call, so we
    // only need to fetch once for the whole portfolio.
    if (Object.keys(matchProfilesById).length > 0) {
      setCardsStatsReady(true);
      return;
    }

    setCardsStatsReady(false);
    let isActive = true;
    const fetchMatchProfiles = async () => {
      try {
        const response = await API.getMentorMatches({ orgId, groupId });
        if (response?.status === 200 && isActive) {
          const profiles = Array.isArray(response?.data?.profiles) ? response.data.profiles : [];
          const profilesById: Record<string, any> = {};
          profiles.forEach((profile: any) => {
            if (profile?.matchId) profilesById[profile.matchId] = profile;
          });
          setMatchProfilesById(profilesById);
        }
      } catch (error) {
        // no-op
      }
      if (isActive) setCardsStatsReady(true);
    };
    fetchMatchProfiles();

    return () => {
      isActive = false;
    };
  }, [myMenteesList, orgId, groupId, matchProfilesById]);

  const normalizeMatchId = (value: any) => {
    if (!value) return '';
    return String(value);
  };

  const getMatchIdForMentee = useCallback(
    (mentee: any) => {
      const menteeKey = mentee?.menteeId ?? mentee?.id ?? mentee?.userId;
      if (!menteeKey) return '';
      const raw =
        mentee?.matchId ??
        mentee?.confirmMatchId ??
        mentee?.match_id ??
        (mentee?.match && (mentee.match.id ?? mentee.match._id ?? mentee.match.matchId));
      if (raw) return normalizeMatchId(raw);
      const fromList = Array.isArray(myMenteesList)
        ? myMenteesList.find((m: any) => {
            const k = m?.menteeId ?? m?.id ?? m?.userId;
            return k && String(k) === String(menteeKey);
          })
        : null;
      const id = fromList?.matchId ?? fromList?.confirmMatchId ?? fromList?.match_id ?? (fromList?.match && (fromList.match.id ?? fromList.match._id ?? fromList.match.matchId));
      return id ? normalizeMatchId(id) : '';
    },
    [myMenteesList]
  );

  const handleOpenMeetingModal = (mentee: any, type: 'history' | 'future', objectiveId?: string | null) => {
    setActiveMeetingModal({
      type,
      mentee,
      objectiveId: objectiveId ?? mentee?.objectiveId ?? null
    });
  };

  const handleCloseMeetingModal = () => {
    setActiveMeetingModal({ type: null, mentee: null, objectiveId: null });
    setMeetingHistoryFromApi(null);
    setMeetingHistoryLoading(false);
  };

  const fetchUserLearningObjectives = useCallback(async () => {
    if (!isMentorship || !orgId) return;
    try {
      const response = await API.UserLearningObjectives({ orgId });
      const payload = response?.data?.data || response?.data || [];
      setUserLearningObjectives(Array.isArray(payload) ? payload : []);
    } catch (error) {
      setUserLearningObjectives([]);
    }
  }, [isMentorship, orgId]);

  useEffect(() => {
    const { type, mentee } = activeMeetingModal;
    if ((type !== 'history' && type !== 'future') || !mentee || !orgId) return;
    const matchId = getMatchIdForMentee(mentee);
    if (!matchId) {
      setMeetingHistoryFromApi({ past: [], upcoming: [], canceled: [] });
      setMeetingHistoryLoading(false);
      return;
    }
    // Load review/details from UserLearningObjectives (callDetails, callReview, menteeRating, mentorRating)
    fetchUserLearningObjectives();
    let cancelled = false;

    // Prefer the meeting history already loaded by the consolidated mentor-matches
    // call; only hit the network if this match wasn't part of that payload.
    const cachedProfile = matchProfilesById[matchId];
    const cachedHistory = cachedProfile?.meetingHistory;
    if (cachedHistory) {
      setMeetingHistoryFromApi({
        past: Array.isArray(cachedHistory.past) ? cachedHistory.past : [],
        upcoming: Array.isArray(cachedHistory.upcoming) ? cachedHistory.upcoming : [],
        canceled: Array.isArray(cachedHistory.canceled) ? cachedHistory.canceled : []
      });
      setMeetingHistoryLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setMeetingHistoryLoading(true);
    setMeetingHistoryFromApi(null);
    API.getMatchProfile({ orgId, confirmMatchId: matchId })
      .then((response) => {
        if (cancelled) return;
        const data = response?.data?.data ?? response?.data;
        const meetingHistory = data?.meetingHistory ?? {};
        setMeetingHistoryFromApi({
          past: Array.isArray(meetingHistory.past) ? meetingHistory.past : [],
          upcoming: Array.isArray(meetingHistory.upcoming) ? meetingHistory.upcoming : [],
          canceled: Array.isArray(meetingHistory.canceled) ? meetingHistory.canceled : []
        });
      })
      .catch(() => {
        if (!cancelled) setMeetingHistoryFromApi({ past: [], upcoming: [], canceled: [] });
      })
      .finally(() => {
        if (!cancelled) setMeetingHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Use stable id so effect doesn't re-run when mentee object reference changes (avoids infinite loop)
  }, [
    activeMeetingModal.type,
    activeMeetingModal.mentee?.menteeId ?? activeMeetingModal.mentee?.id ?? activeMeetingModal.mentee?.userId,
    orgId,
    getMatchIdForMentee,
    fetchUserLearningObjectives
  ]);

  const grpDetails = useAppSelector((state) => state.groupDetails.data);
  const sessionsPerMatch = grpDetails?.matchesSettings?.sessionsPerMatch || 1;
  const getLiveSessions = (length: number) => {
    const value = Math.round((length / sessionsPerMatch) * 100);
    return isNaN(value) ? 0 : value > 100 ? 100 : value;
  };
  const getObjectiveMentees = async ({
    orgId,
    location,
    groupId
  }: {
    orgId: string;
    location: string;
    groupId: string;
  }) => {
    try {
      const response = await API.getMenteeListForMentor(orgId, location, groupId);
      if (response.status === 200) {
        const transformedData = response?.data?.data.map((item: any) => ({
          ...item,
          displayName: item.menteeName
        }));
        setMyMenteesList(transformedData);
      }
    } catch (e) {
      console.error('Failed to fetch mentees:', e);
    }
  };

  const {
    data: objectives,
    isLoading: objLoading,
    refetch: fetchObjectives,
    isError: objError
  } = useLearningObjs({ orgId, grpId: groupId });

  const learningObjectives = objectives?.data?.data || [];
  const getObjectiveId = (obj: any, index: number) =>
    obj?.objectiveId || obj?.id || obj?._id || `${obj?.objective || 'objective'}-${index}`;

  const groupLearningObjectives = grpDetails?.learningObjectives?.objectives || [];
  useEffect(() => {
    if (!isMentorship || !orgId) return;
    fetchUserLearningObjectives();
  }, [fetchUserLearningObjectives, isMentorship, orgId]);
  const groupObjectiveIds = useMemo(
    () => new Set(groupLearningObjectives.map((obj: any) => obj?.id).filter(Boolean)),
    [groupLearningObjectives]
  );
  const matchedObjectivesMap = useMemo(() => {
    const map = new Map<string, string>();
    (myMenteesList || []).forEach((mentee: any) => {
      (mentee?.matchedObjectives || []).forEach((obj: any) => {
        const id = obj?.objectiveId || obj?.id || obj?._id;
        if (!id) return;
        const name = obj?.objective || obj?.name || `Objective ${id}`;
        if (!map.has(id)) {
          map.set(id, name);
        }
      });
    });
    return map;
  }, [myMenteesList]);
  const displayObjectives = useMemo(() => {
    const source = isMentorship ? userLearningObjectives : groupLearningObjectives;
    return (Array.isArray(source) ? source : []).map((obj: any, idx: number) => {
      const id = obj?.objectiveId || obj?.id || obj?._id || `${obj?.objective || 'objective'}-${idx}`;
      const objective =
        obj?.objective ||
        obj?.name ||
        matchedObjectivesMap.get(String(id)) ||
        `Objective ${idx + 1}`;
      return { id, objective };
    });
  }, [groupLearningObjectives, userLearningObjectives, matchedObjectivesMap, isMentorship]);
  useEffect(() => {
    if (!displayObjectives.length) {
      setActiveExpertiseIndex(0);
      return;
    }
    setActiveExpertiseIndex((prev) =>
      prev >= displayObjectives.length ? displayObjectives.length - 1 : prev
    );
  }, [displayObjectives.length]);

  const objectiveMentees = useMemo(() => {
    const map: Record<string, any[]> = {};
    (myMenteesList || []).forEach((mentee: any) => {
      const matched = mentee?.matchedObjectives || [];
      matched.forEach((obj: any) => {
        const id = obj?.objectiveId || obj?.id || obj?._id || obj?.objective;
        if (!id) return;
        if (!map[id]) map[id] = [];
        map[id].push({
          ...mentee,
          objective: obj?.objective || obj?.name,
          objectiveId: id,
          objectiveStatus: obj?.status
        });
      });
    });
    return map;
  }, [myMenteesList]);

  const activeObjective = displayObjectives[activeExpertiseIndex];
  const activeObjectiveId = activeObjective ? getObjectiveId(activeObjective, activeExpertiseIndex) : '';
  const menteesForActiveObjective = activeObjectiveId ? objectiveMentees[activeObjectiveId] || [] : [];
  const primaryMentee = menteesForActiveObjective[0] || null;
  const primaryObjectiveStatus = primaryMentee?.matchedObjectives?.[0]?.status || 'to-do';
  const normalizedPrimaryStatus = primaryObjectiveStatus?.toString()?.toLowerCase() || '';
  const isObjectiveComplete = ['completed', 'complete', 'done'].includes(normalizedPrimaryStatus);
  const menteeStatusLabel = getStatusText(primaryObjectiveStatus);
  const menteeStatusColor = getStatusColor(primaryObjectiveStatus);
  const menteeNameParts = primaryMentee ? getDisplayName(primaryMentee).split(' ') : [];
  const menteeStats = useMemo(() => calculateMenteeStats(primaryMentee), [primaryMentee]);
  const menteeOrdinal =
    primaryMentee && menteesForActiveObjective.length
      ? menteesForActiveObjective.findIndex(
          (mentee) =>
            mentee?.id === primaryMentee?.id ||
            mentee?.menteeId === primaryMentee?.menteeId ||
            mentee?.displayName === primaryMentee?.displayName
        )
      : -1;
  const menteeLabel = `${w('mentee')} ${menteeOrdinal >= 0 ? menteeOrdinal + 1 : 1}`;
  const expertiseLabel = `Area of Expertise ${activeExpertiseIndex + 1}`;
  const expertiseTitle = activeObjective?.objective || `Objective ${activeExpertiseIndex + 1}`;
  const canGoNext = activeExpertiseIndex < displayObjectives.length - 1;
  const canGoPrev = activeExpertiseIndex > 0;
  const handleNextExpertise = () => {
    setActiveExpertiseIndex((prev) => Math.min(prev + 1, Math.max(displayObjectives.length - 1, 0)));
  };
  const handlePrevExpertise = () => {
    setActiveExpertiseIndex((prev) => Math.max(prev - 1, 0));
  };

  useEffect(() => {
    setActiveMenteeIndex(0);
    const container = menteeSliderScrollRef.current;
    if (container) container.scrollTo({ left: 0, behavior: 'smooth' });
  }, [activeObjectiveId]);

  const updateMenteeCardIndexFromScroll = useCallback(() => {
    const container = menteeSliderScrollRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const viewportCenterX = containerRect.left + containerRect.width / 2;
    let bestIndex = 0;
    let bestDistance = Infinity;
    const refs = menteeCardRefs.current;
    for (let i = 0; i < refs.length; i++) {
      const card = refs[i];
      if (!card) continue;
      const cardRect = card.getBoundingClientRect();
      const cardCenterX = cardRect.left + cardRect.width / 2;
      const distance = Math.abs(cardCenterX - viewportCenterX);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = i;
      }
    }
    setActiveMenteeIndex(bestIndex);
  }, []);

  useEffect(() => {
    const container = menteeSliderScrollRef.current;
    if (!container) return;
    updateMenteeCardIndexFromScroll();
    container.addEventListener('scroll', updateMenteeCardIndexFromScroll);
    return () => container.removeEventListener('scroll', updateMenteeCardIndexFromScroll);
  }, [activeObjectiveId, updateMenteeCardIndexFromScroll]);

  const scrollMenteeToIndex = useCallback((targetIndex: number) => {
    const container = menteeSliderScrollRef.current;
    const cardEl = menteeCardRefs.current[targetIndex];
    if (!container) return;
    if (targetIndex <= 0) {
      container.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }
    if (cardEl) {
      const containerRect = container.getBoundingClientRect();
      const cardRect = cardEl.getBoundingClientRect();
      const containerCenterX = containerRect.left + containerRect.width / 2;
      const cardCenterX = cardRect.left + cardRect.width / 2;
      const delta = cardCenterX - containerCenterX;
      const targetScroll = container.scrollLeft + delta;
      const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
      container.scrollTo({
        left: Math.max(0, Math.min(targetScroll, maxScroll)),
        behavior: 'smooth'
      });
    } else {
      const cardStep = MENTEE_CARD_WIDTH + MENTEE_CARD_GAP;
      const targetScroll = Math.max(0, targetIndex * cardStep);
      const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
      container.scrollTo({ left: Math.min(targetScroll, maxScroll), behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (!displayObjectives.length) return;
    const firstId = getObjectiveId(displayObjectives[0], 0);
    setExpandedObjectives((prev) => (prev[firstId] ? prev : { [firstId]: true }));
  }, [displayObjectives]);

  const handleFindNewMentee = (objId: string, name: string) => {
    setRecruitObjId(objId);
    setRecruitObjectiveName(name);
    setRecruitModalOpen(true);
  };

  const renderMenteeStat = (
    value: number,
    label: string,
    highlight: boolean,
    onClick: (() => void) | undefined,
    isActive: boolean,
    statBg: string,
    statValueColor: string
  ) => {
    let IconCmp: React.ElementType | null = null;
    if (label.toLowerCase().includes('message')) IconCmp = ForumIcon;
    else if (label.toLowerCase().includes('history')) IconCmp = RestoreOutlinedIcon;
    else if (label.toLowerCase().includes('future')) IconCmp = CalendarTodayOutlinedIcon;
    const bg = statBg ?? 'var(--Fill-background, #0F0F0F)';
    const valueColor = statValueColor ?? '#FDFDFD';
    return (
      <Box
        onClick={onClick}
        sx={{
          alignSelf: 'stretch',
          minHeight: 48,
          p: 1,
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          background: `${bg} !important`,
          cursor: onClick ? 'pointer' : 'default',
          transition: onClick ? 'transform 0.1s ease-in-out' : undefined,
          '&:hover': onClick
            ? { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.25)' }
            : undefined
        }}
      >
        <Box flex={1} display="flex" flexDirection="column" gap={0.25}>
          <Typography sx={{ color: valueColor, fontSize: 16, fontWeight: 600, lineHeight: '20px' }}>{value}</Typography>
          <Typography sx={{ color: 'var(--Text-text-tertiary, #B6B6B6)', fontSize: 12, fontWeight: 600, lineHeight: '16px' }}>{label}</Typography>
        </Box>
        <Box
          sx={{
            width: highlight ? 36 : 28,
            height: highlight ? 36 : 28,
            borderRadius: '999px',
            background: highlight
              ? 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)'
              : 'var(--Fill-Button-button-gray, #1C1C1C)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {IconCmp ? <IconCmp sx={{ color: '#FDFDFD', fontSize: highlight ? 20 : 16 }} /> : null}
        </Box>
      </Box>
    );
  };

  const renderMenteeCard = () => {
    const handleAddClick = () => {
      if (!activeObjectiveId) return;
      handleFindNewMentee(activeObjectiveId, expertiseTitle);
    };

    if (!primaryMentee) {
      return (
        <Box
          sx={{
            width: MENTEE_CARD_WIDTH,
            minHeight: MENTEE_CARD_HEIGHT,
            p: 2,
            background: 'var(--Fill-Card-card-highlight, #1C1C1C)',
            borderRadius: 1.5,
            outline: '1px solid var(--Stroke-Card-card-highlight, #FDFDFD)',
            outlineOffset: '-1px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2.5,
            cursor: activeObjectiveId ? 'pointer' : 'not-allowed'
          }}
          onClick={activeObjectiveId ? handleAddClick : undefined}
        >
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 600,
              color: '#FDFDFD',
              textTransform: 'capitalize'
            }}
          >
            Add {w('mentee')}
          </Typography>
          <Box
            sx={{
              width: 34,
              height: 34,
              // borderRadius: '50%',
              background: '#1C1C1C',
              // border: '1px solid var(--Stroke-Card-card-highlight, #FDFDFD)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PlusOutlined color="white" style={{ color: 'white' }} size={16} />
          </Box>
        </Box>
      );
    }

    const avatarUrl =
      primaryMentee?.headshot ||
      primaryMentee?.photo ||
      primaryMentee?.picture ||
      primaryMentee?.displayPicture ||
      undefined;
    const primaryMatchId = primaryMentee?.matchId ? primaryMentee.matchId : getMatchIdForMentee(primaryMentee);
    const primaryMatchProfile = primaryMatchId ? matchProfilesById[primaryMatchId] : null;
    const primaryCurrentObjId = primaryMentee?.objectiveId ?? activeObjectiveId ?? '';
    const primaryMeetingHistoryApi = primaryMatchProfile?.meetingHistory;
    const primaryPastForObjective = (primaryMeetingHistoryApi?.past ?? []).filter(
      (m: any) => m?.objective != null && String(m.objective) === String(primaryCurrentObjId)
    );
    const primaryUpcomingForObjective = (primaryMeetingHistoryApi?.upcoming ?? []).filter(
      (m: any) => m?.objective != null && String(m.objective) === String(primaryCurrentObjId)
    );
    const primaryUseMatchProfileForCounts = primaryMatchProfile && primaryMeetingHistoryApi && primaryCurrentObjId;
    const primaryStats = {
      messagesShared:
        primaryMatchProfile?.messagesShared ?? primaryMatchProfile?.messagedShared ?? menteeStats.messagesShared,
      meetingHistory: primaryUseMatchProfileForCounts
        ? primaryPastForObjective.length
        : (primaryMatchProfile?.pastCall ?? menteeStats.meetingHistory),
      futureMeetings: primaryUseMatchProfileForCounts
        ? primaryUpcomingForObjective.length
        : (primaryMatchProfile?.futureCall ?? menteeStats.futureMeetings)
    };

    if (isObjectiveComplete) {
      return (
        <Box
          sx={{
            width: MENTEE_CARD_WIDTH,
            minHeight: MENTEE_CARD_HEIGHT,
            p: 2,
            background: 'rgba(0, 150.73, 45.45, 0.20)',
            borderRadius: 1.5,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#FDFDFD' }}>{menteeLabel}</Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                flexWrap: 'wrap'
              }}
            >
              {menteeNameParts.length ? (
                <>
                  <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#FDFDFD' }}>{menteeNameParts[0]}</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#FDFDFD' }}>
                    {menteeNameParts.slice(1).join(' ') || ''}
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#FDFDFD' }}>
                  Mentor {w('mentee', { lower: true, article: true })}
                </Typography>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 48,
                p: 1,
                background: 'rgba(0, 0, 0, 0.5)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography sx={{ color: '#FDFDFD', fontWeight: 600, fontSize: 14 }}>Objective Complete</Typography>
            </Box>
            <Box
              sx={{
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '999px',
                  background: '#00972D',
                  outline: '6px solid rgba(255, 255, 255, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CheckIcon sx={{ color: '#FDFDFD', fontSize: 24 }} />
              </Box>
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          width: MENTEE_CARD_WIDTH,
          minHeight: MENTEE_CARD_HEIGHT,
          p: 2,
          borderRadius: '6px',
          outline: '1px solid var(--Stroke-Card-card-highlight, #FDFDFD)',
          outlineOffset: '-1px',
          background: 'var(--Fill-Card-card-highlight, #1C1C1C)',
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5
        }}
      >
        <Box display="flex" alignItems="center" gap={1} width="100%">
          <ChatAvatarComponent
            image={avatarUrl}
            firstLetter={menteeNameParts[0]?.charAt(0) || ''}
            width="40px"
            height="40px"
            type="noStatus"
          />
          <Box flex={1} display="flex" flexDirection="column" gap={0.25} minWidth={0}>
            <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#B6B6B6', lineHeight: '12px' }}>{menteeLabel}</Typography>
            <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
              {menteeNameParts.length ? (
                <>
                  <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#FDFDFD', lineHeight: '20px' }}>{menteeNameParts[0]}</Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#FDFDFD', lineHeight: '20px' }}>
                    {menteeNameParts.slice(1).join(' ') || ''}
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#FDFDFD', lineHeight: '20px' }}>
                  Add your first {w('mentee', { lower: true })}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DosenEllipsisMenu onViewProfile={() => handleProfileClick(primaryMentee)} />
          </Box>
        </Box>
        <Stack spacing={1} width="100%">
          {renderMenteeStat(
            primaryStats.messagesShared,
            'Messages Shared',
            true,
            primaryMentee ? () => navigateMember(primaryMentee) : undefined,
            true,
            'var(--Fill-background, #0F0F0F)',
            '#FDFDFD'
          )}
          {renderMenteeStat(
            primaryStats.meetingHistory,
            'Meeting History',
            false,
            primaryMentee ? () => handleOpenMeetingModal(primaryMentee, 'history', primaryMentee?.objectiveId ?? activeObjectiveId) : undefined,
            true,
            'var(--Fill-background, #0F0F0F)',
            '#FDFDFD'
          )}
          {renderMenteeStat(
            primaryStats.futureMeetings,
            'Future Meetings',
            false,
            primaryMentee ? () => handleOpenMeetingModal(primaryMentee, 'future', primaryMentee?.objectiveId ?? activeObjectiveId) : undefined,
            true,
            'var(--Fill-background, #0F0F0F)',
            '#FDFDFD'
          )}
        </Stack>
      </Box>
    );
  };

  const renderMentorshipMatchesList = () => {
    const objectives = displayObjectives || [];
    if (!objectives.length) {
      return (
        <Box
          sx={{
            width: '100%',
            minHeight: 200,
            borderRadius: 1.5,
            border: '1px solid #2A2A2A',
            background: 'var(--Fill-Background-secondary, #101010)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography sx={{ color: '#868686', fontSize: 14 }}>No objectives.</Typography>
        </Box>
      );
    }

    const activeTitle = activeObjective?.objective || `Objective ${activeExpertiseIndex + 1}`;
    const mentees = activeObjectiveId ? objectiveMentees[activeObjectiveId] || [] : [];
    const menteeCards = [...mentees, { isAddCard: true }];
    const totalMenteeCards = menteeCards.length;
    const menteePrevDisabled = activeMenteeIndex <= 0;
    const menteeNextDisabled = activeMenteeIndex >= totalMenteeCards - 1;

    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}
        >
          <Box
            sx={{
              position: 'relative',
              pt: 2,
              pb: 4,
              minHeight: MENTEE_CARD_HEIGHT
            }}
          >
            <Box
              ref={menteeSliderScrollRef}
              sx={{
                width: '100%',
                minWidth: 0,
                overflowX: 'auto',
                overflowY: 'hidden',
                display: 'block',
                scrollSnapType: 'x mandatory',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none'
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  gap: MENTEE_CARD_GAP,
                  px: `calc(50% - ${MENTEE_CARD_WIDTH / 2}px)`,
                  py: 1,
                  boxSizing: 'content-box',
                  minWidth: 'min-content'
                }}
              >
                {menteeCards.map((item: any, index: number) => {
                  const isAddCard = item?.isAddCard === true;
                  if (isAddCard) {
                    return (
                      <Box
                        key="add-mentee"
                        sx={{
                          width: MENTEE_CARD_WIDTH,
                          flex: '0 0 auto',
                          scrollSnapAlign: 'center',
                          position: 'relative'
                        }}
                        ref={(el) => {
                          menteeCardRefs.current[index] = el as HTMLDivElement | null;
                        }}
                      >
                        <Box
                          onClick={activeObjectiveId ? () => handleFindNewMentee(activeObjectiveId, expertiseTitle) : undefined}
                          sx={{
                            cursor: activeObjectiveId ? 'pointer' : 'not-allowed',
                            width: MENTEE_CARD_WIDTH,
                            minHeight: MENTEE_CARD_HEIGHT,
                            p: 2,
                            borderRadius: '6px',
                            outline: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                            outlineOffset: '-1px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2.5,
                            textAlign: 'center',
                            background: 'transparent'
                          }}
                        >
                          <Typography fontSize={18} fontWeight={600} sx={{ color: 'var(--Text-text-secondary, #868686)' }}>
                            Add {w('mentee')}
                          </Typography>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '14px',
                              background: 'var(--Fill-Button-button-gray, #1C1C1C)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid #2A2A2A'
                            }}
                          >
                            <AddIcon sx={{ width: 16, height: 16, color: '#FDFDFD' }} />
                          </Box>
                        </Box>
                      </Box>
                    );
                  }
                  const mentee = item;
                  const statusValue =
                    mentee?.objectiveStatus ||
                    mentee?.matchedObjectives?.[0]?.status ||
                    mentee?.matchStatus ||
                    mentee?.status ||
                    'to-do';
                  const normalizedStatus = statusValue?.toString()?.toLowerCase() || '';
                  const isComplete = ['completed', 'complete', 'done'].includes(normalizedStatus);
                  const menteeLabel = `${w('mentee')} ${index + 1}`;
                  const menteeNameParts = mentee ? getDisplayName(mentee).split(' ') : [];
                  const avatarUrl =
                    mentee?.headshot ||
                    mentee?.photo ||
                    mentee?.picture ||
                    mentee?.displayPicture ||
                    undefined;
                  const stats = calculateMenteeStats(mentee);
                  const rawMatchId = mentee?.matchId ?? getMatchIdForMentee(mentee);
                  const matchId = normalizeMatchId(rawMatchId);
                  const matchProfile = matchId ? matchProfilesById[matchId] : null;
                  const currentObjId = mentee?.objectiveId ?? '';
                  const meetingHistoryApi = matchProfile?.meetingHistory;
                  const pastForObjective = (meetingHistoryApi?.past ?? []).filter(
                    (m: any) => m?.objective != null && String(m.objective) === String(currentObjId)
                  );
                  const upcomingForObjective = (meetingHistoryApi?.upcoming ?? []).filter(
                    (m: any) => m?.objective != null && String(m.objective) === String(currentObjId)
                  );
                  const useMatchProfileForCounts = matchProfile && meetingHistoryApi && currentObjId;
                  const matchStats = {
                    messagesShared:
                      matchProfile?.messagesShared ?? matchProfile?.messagedShared ?? stats.messagesShared,
                    meetingHistory: useMatchProfileForCounts
                      ? pastForObjective.length
                      : (matchProfile?.pastCall ?? stats.meetingHistory),
                    futureMeetings: useMatchProfileForCounts
                      ? upcomingForObjective.length
                      : (matchProfile?.futureCall ?? stats.futureMeetings)
                  };
                  const isActiveCard = index === activeMenteeIndex;
                  const cardBaseStyles = {
                    width: `${MENTEE_CARD_WIDTH}px !important`,
                    flex: '0 0 auto',
                    height: `${MENTEE_CARD_HEIGHT}px !important`,
                    borderRadius: 1.5,
                    boxSizing: 'border-box',
                    scrollSnapAlign: 'center',
                    transition: 'transform 200ms ease, box-shadow 200ms ease',
                    transform: isActiveCard ? 'scale(1.02)' : 'scale(0.98)',
                    boxShadow: isActiveCard ? '0px 20px 40px rgba(0,0,0,0.55)' : 'none',
                    zIndex: isActiveCard ? 2 : 1,
                    '&::after': !isActiveCard
                      ? {
                          content: '""',
                          position: 'absolute',
                          inset: 0,
                          background: 'rgba(0,0,0,0.35)',
                          borderRadius: 'inherit',
                          pointerEvents: 'none'
                        }
                      : {}
                  };

                  if (isComplete) {
                    return (
                      <Box
                        key={mentee?.matchId || mentee?.menteeId || mentee?.id || `${activeObjectiveId}-${index}`}
                        sx={{
                          width: MENTEE_CARD_WIDTH,
                          flex: '0 0 auto',
                          scrollSnapAlign: 'center',
                          position: 'relative'
                        }}
                        ref={(el) => {
                          menteeCardRefs.current[index] = el as HTMLDivElement | null;
                        }}
                      >
                        <Box
                          sx={{
                            p: 2,
                            background: 'rgba(0, 150.73, 45.45, 0.20)',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            overflow: 'hidden',
                            width: '100%',
                            height: MENTEE_CARD_HEIGHT,
                            borderRadius: 1.5,
                            boxSizing: 'border-box',
                            transition: 'transform 200ms ease, box-shadow 200ms ease',
                            transform: isActiveCard ? 'scale(1.02)' : 'scale(0.98)',
                            boxShadow: isActiveCard ? '0px 20px 40px rgba(0,0,0,0.55)' : 'none',
                            zIndex: isActiveCard ? 2 : 1,
                            '&::after': !isActiveCard
                              ? {
                                  content: '""',
                                  position: 'absolute',
                                  inset: 0,
                                  background: 'rgba(0,0,0,0.35)',
                                  borderRadius: 'inherit',
                                  pointerEvents: 'none'
                                }
                              : {}
                          }}
                        >
                        <Box
                          sx={{
                            position: 'relative',
                            zIndex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5
                          }}
                        >
                          <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#FDFDFD' }}>
                            {menteeLabel}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
                            {menteeNameParts.length ? (
                              <>
                                <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#FDFDFD', lineHeight: '20px' }}>
                                  {menteeNameParts[0]}
                                </Typography>
                                <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#FDFDFD', lineHeight: '20px' }}>
                                  {menteeNameParts.slice(1).join(' ') || ''}
                                </Typography>
                              </>
                            ) : (
                              <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#FDFDFD', lineHeight: '20px' }}>
                                Mentor {w('mentee', { lower: true, article: true })}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            position: 'relative',
                            zIndex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1.5
                          }}
                        >
                          <Box
                            sx={{
                              flex: 1,
                              minHeight: 48,
                              p: 1,
                              background: 'rgba(0, 0, 0, 0.5)',
                              borderRadius: 1.25,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Typography sx={{ color: '#FDFDFD', fontWeight: 600, fontSize: 12 }}>
                              Objective Complete
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '999px',
                                background: '#00972D',
                                outline: '4px solid rgba(255, 255, 255, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <CheckIcon sx={{ color: '#FDFDFD', fontSize: 20 }} />
                            </Box>
                          </Box>
                        </Box>
                        </Box>
                      </Box>
                    );
                  }

                  return (
                    <Box
                      key={mentee?.matchId || mentee?.menteeId || mentee?.id || `${activeObjectiveId}-${index}`}
                      sx={{
                        width: MENTEE_CARD_WIDTH,
                        flex: '0 0 auto',
                        scrollSnapAlign: 'center',
                        position: 'relative'
                      }}
                      ref={(el) => {
                        menteeCardRefs.current[index] = el as HTMLDivElement | null;
                      }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: '6px',
                          outline: `1px solid ${isActiveCard ? 'var(--Stroke-Card-card-highlight, #FDFDFD)' : 'var(--Stroke-Border-border-dim, #2A2A2A)'}`,
                          outlineOffset: '-1px',
                          background: isActiveCard ? 'var(--Fill-Card-card-highlight, #1C1C1C)' : 'transparent',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2.5,
                          width: '100%',
                          height: MENTEE_CARD_HEIGHT,
                          boxSizing: 'border-box',
                          transition: 'transform 200ms ease, box-shadow 200ms ease',
                          transform: isActiveCard ? 'scale(1.02)' : 'scale(0.98)',
                          boxShadow: isActiveCard ? '0px 20px 40px rgba(0,0,0,0.55)' : 'none',
                          zIndex: isActiveCard ? 2 : 1,
                          '&::after': !isActiveCard
                            ? {
                                content: '""',
                                position: 'absolute',
                                inset: 0,
                                background: 'rgba(0,0,0,0.35)',
                                borderRadius: 'inherit',
                                pointerEvents: 'none'
                              }
                            : {}
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1} width="100%">
                          <Box sx={{ flexShrink: 0, opacity: isActiveCard ? 1 : 0.5 }}>
                            <ChatAvatarComponent
                              image={avatarUrl}
                              firstLetter={menteeNameParts[0]?.charAt(0) || ''}
                              width="40px"
                              height="40px"
                              type="noStatus"
                            />
                          </Box>
                          <Box flex={1} display="flex" flexDirection="column" gap={0.25} minWidth={0}>
                            <Typography sx={{ color: isActiveCard ? '#B6B6B6' : '#6B6B6B', fontSize: 9, fontWeight: 700, lineHeight: '12px' }}>
                              {menteeLabel}
                            </Typography>
                            <Box display="flex" gap={0.25} alignItems="center" flexWrap="wrap">
                              <Typography sx={{ color: isActiveCard ? '#FDFDFD' : '#868686', fontSize: 16, fontWeight: 600, lineHeight: '20px' }} noWrap>
                                {menteeNameParts[0] || ''}
                              </Typography>
                              {menteeNameParts.length > 1 && (
                                <Typography sx={{ color: isActiveCard ? '#FDFDFD' : '#868686', fontSize: 16, fontWeight: 600, lineHeight: '20px' }} noWrap>
                                  {menteeNameParts.slice(1).join(' ')}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FDFDFD' }}>
                            <DosenEllipsisMenu onViewProfile={() => handleProfileClick(mentee)} />
                          </Box>
                        </Box>

                        <Stack spacing={1} width="100%">
                          {renderMenteeStat(
                            matchStats.messagesShared,
                            'Messages Shared',
                            true,
                            mentee ? () => navigateMember(mentee) : undefined,
                            isActiveCard,
                            isActiveCard ? 'var(--Fill-background, #0F0F0F)' : 'rgba(255,255,255,0.04)',
                            isActiveCard ? '#FDFDFD' : '#B6B6B6'
                          )}
                          {renderMenteeStat(
                            matchStats.meetingHistory,
                            'Meeting History',
                            false,
                            mentee ? () => handleOpenMeetingModal(mentee, 'history', mentee?.objectiveId) : undefined,
                            isActiveCard,
                            isActiveCard ? 'var(--Fill-background, #0F0F0F)' : 'rgba(255,255,255,0.04)',
                            isActiveCard ? '#FDFDFD' : '#B6B6B6'
                          )}
                          {renderMenteeStat(
                            matchStats.futureMeetings,
                            'Future Meetings',
                            false,
                            mentee ? () => handleOpenMeetingModal(mentee, 'future', mentee?.objectiveId) : undefined,
                            isActiveCard,
                            isActiveCard ? 'var(--Fill-background, #0F0F0F)' : 'rgba(255,255,255,0.04)',
                            isActiveCard ? '#FDFDFD' : '#B6B6B6'
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
            <IconButton
              disabled={menteePrevDisabled}
              onClick={() => scrollMenteeToIndex(Math.max(0, activeMenteeIndex - 1))}
              sx={{
                position: 'absolute',
                left: `calc(50% - ${MENTEE_CARD_WIDTH / 2 + 48 + 4}px)`,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1,
                width: 48,
                height: 96,
                background: menteePrevDisabled
                  ? 'var(--Fill-Carousel-button-disabled, rgba(0, 0, 0, 0.10))'
                  : 'var(--Fill-Carousel-button-default/50, rgba(0, 0, 0, 0.50))',
                borderRadius: '8px 0 0 8px',
                border: '1px solid var(--Stroke-Carousel-button, #2A2A2A)',
                borderRight: 'none',
                '&:hover': menteePrevDisabled ? {} : { background: 'rgba(0, 0, 0, 0.65)' },
                '&.Mui-disabled': { borderColor: '#2A2A2A' }
              }}
            >
              <ArrowBackIosNewOutlinedIcon fontSize="small" sx={{ color: menteePrevDisabled ? '#444' : '#FDFDFD' }} />
            </IconButton>
            <IconButton
              disabled={menteeNextDisabled}
              onClick={() => {
                if (activeMenteeIndex >= totalMenteeCards - 1 && menteeCards[totalMenteeCards - 1]?.isAddCard) {
                  handleFindNewMentee(activeObjectiveId!, expertiseTitle);
                  return;
                }
                scrollMenteeToIndex(Math.min(activeMenteeIndex + 1, totalMenteeCards - 1));
              }}
              sx={{
                position: 'absolute',
                right: `calc(50% - ${MENTEE_CARD_WIDTH / 2 + 48 + 4}px)`,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1,
                width: 48,
                height: 96,
                background: menteeNextDisabled
                  ? 'var(--Fill-Carousel-button-disabled, rgba(0, 0, 0, 0.10))'
                  : 'var(--Fill-Carousel-button-default/50, rgba(0, 0, 0, 0.50))',
                borderRadius: '0 8px 8px 0',
                border: '1px solid var(--Stroke-Carousel-button, #2A2A2A)',
                borderLeft: 'none',
                '&:hover': menteeNextDisabled ? {} : { background: 'rgba(0, 0, 0, 0.65)' }
              }}
            >
              {menteeCards[activeMenteeIndex]?.isAddCard ? (
                <AddIcon fontSize="small" sx={{ color: '#FDFDFD' }} />
              ) : (
                <ArrowForwardIosOutlinedIcon fontSize="small" sx={{ color: '#FDFDFD' }} />
              )}
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderHybridMatchesList = () => {
    const matches: any[] = Array.isArray(myMenteesList) ? myMenteesList : [];
    if (!matches.length) {
      return (
        <Box
          sx={{
            width: '100%',
            minHeight: 200,
            borderRadius: 1.5,
            border: '1px solid #2A2A2A',
            background: 'var(--Fill-Background-secondary, #101010)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography sx={{ color: '#868686', fontSize: 14 }}>No active matches yet.</Typography>
        </Box>
      );
    }

    // Hybrid matches are independent (no shared objectives), so render them as a
    // single responsive grid using the same polished card design as the
    // mentorship view.
    return (
      <Box display="flex" flexWrap="wrap" gap={2} width="100%">
        {matches.map((mentee: any, index: number) => {
          const key = mentee?.matchId || mentee?.menteeId || mentee?.id || `match-${index}`;
          const menteeLabel = `${w('mentee')} ${index + 1}`;
          const menteeNameParts = getDisplayName(mentee)?.split(' ') ?? [];
          const avatarUrl = mentee?.headshot || mentee?.photo || mentee?.picture || mentee?.displayPicture || undefined;
          const stats = calculateMenteeStats(mentee);
          const matchProfile = mentee?.matchId ? matchProfilesById[mentee.matchId] : null;
          const matchStats = {
            messagesShared: matchProfile?.messagesShared ?? matchProfile?.messagedShared ?? stats.messagesShared,
            meetingHistory: matchProfile?.pastCall ?? stats.meetingHistory,
            futureMeetings: matchProfile?.futureCall ?? stats.futureMeetings
          };

          return (
            <Box
              key={key}
              sx={{
                flex: '1 1 320px',
                minWidth: 300,
                maxWidth: 400,
                p: 2,
                borderRadius: '6px',
                outline: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                outlineOffset: '-1px',
                background: 'var(--Fill-Card-card-highlight, #1C1C1C)',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                boxSizing: 'border-box'
              }}
            >
              <Box display="flex" alignItems="center" gap={1} width="100%">
                <Box sx={{ flexShrink: 0 }}>
                  <ChatAvatarComponent
                    image={avatarUrl}
                    firstLetter={menteeNameParts[0]?.charAt(0) || ''}
                    width="40px"
                    height="40px"
                    type="noStatus"
                  />
                </Box>
                <Box flex={1} display="flex" flexDirection="column" gap={0.25} minWidth={0}>
                  <Typography sx={{ color: '#B6B6B6', fontSize: 9, fontWeight: 700, lineHeight: '12px' }}>
                    {menteeLabel}
                  </Typography>
                  <Typography
                    sx={{
                      color: '#FDFDFD',
                      fontSize: 16,
                      fontWeight: 600,
                      lineHeight: '20px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {getDisplayName(mentee)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FDFDFD' }}>
                  <DosenEllipsisMenu onViewProfile={() => handleProfileClick(mentee)} />
                </Box>
              </Box>

              <Stack spacing={1} width="100%">
                {renderMenteeStat(
                  matchStats.messagesShared,
                  'Messages Shared',
                  true,
                  () => navigateMember(mentee),
                  true,
                  'var(--Fill-background, #0F0F0F)',
                  '#FDFDFD'
                )}
                {renderMenteeStat(
                  matchStats.meetingHistory,
                  'Meeting History',
                  false,
                  () => handleOpenMeetingModal(mentee, 'history'),
                  true,
                  'var(--Fill-background, #0F0F0F)',
                  '#FDFDFD'
                )}
                {renderMenteeStat(
                  matchStats.futureMeetings,
                  'Future Meetings',
                  false,
                  () => handleOpenMeetingModal(mentee, 'future'),
                  true,
                  'var(--Fill-background, #0F0F0F)',
                  '#FDFDFD'
                )}
              </Stack>

              <Button
                variant="outlined"
                fullWidth
                onClick={() => handleUserClick(mentee)}
                sx={{
                  color: '#FDFDFD',
                  background: '#1C1C1C',
                  borderColor: '#2A2A2A',
                  textTransform: 'none',
                  fontFamily: 'Inter',
                  fontSize: 13,
                  fontWeight: 600,
                  py: 0.75,
                  '&:hover': { borderColor: '#2A2A2A', background: '#262626' }
                }}
              >
                View Progress
              </Button>
            </Box>
          );
        })}
      </Box>
    );
  };

  const handleAddExpertise = () => {
    setRecruitObjId('');
    setRecruitObjectiveName('');
    setRecruitModalOpen(true);
  };

  const programName = grpDetails?.name || 'Mentorship Program';
  const menteeWordLower = w('mentee', { lower: true });
  const viewDescription = `If you don’t hear from your ${menteeWordLower}, message them and encourage them to schedule a meeting with you.`;
  // Same guidance for hybrid and pure mentorship programs.
  const headerDescription = viewDescription;

  const getMatchId = (mentee: any): string | null => {
    return mentee.matchId || null; // Directly access matchId from the mentee object
  };

  const handleNavigateToChat = () => {
    let communicationId = _.get(selectedMember, 'communicationId', '');
    const listedInCommunity = communityMembers?.find((each) => each?.id?.communicationId === communicationId);
    let isInCommunity = false;
    if (listedInCommunity) {
      isInCommunity = true;
    }
    navigateToChat(communicationId, isInCommunity, personalThreadsArr, navigate, dispatch);
  };

  const { refetch: fetchGroupDetails } = useGroupDetails(groupId, getUserDetails().location);

  useEffect(() => {
    try {
      if (groupId) {
        fetchGroupDetails();
      }
    } catch (error) {
      console.log(error);
    }
  }, [groupId]);

  useEffect(() => {
    if (selectedMember) {
      handleNavigateToChat();
    }
  }, [selectedMember]);

  useEffect(() => {
    if (typeof groupId === 'string' && groupId !== '') {
      getMentorsPeersHomePage(orgId, location, groupId);
      if (isMentorship) {
        getObjectiveMentees({ orgId, location, groupId });
      } else {
        getMyMenteesList({ orgId, groupId });
      }
    }
  }, [groupId, reloadFlag, isMentorship, isHybrid]);

  useEffect(() => {
    if (groupId) {
      fetchObjectives();
    }
  }, [groupId, fetchObjectives]);

  const getMentorHelpfulTips = async () => {
    const response = await API.getMentorHelpfulTips({ orgId: orgId });
    if (response.status === 200) {
      setMentorshipHours(response?.data?.mentorshipHours);
      const formattedMentorROI = new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 1
      }).format(response?.data?.mentorROI);

      setMentorRoi(formattedMentorROI);
    }
  };

  useEffect(() => {
    getMentorHelpfulTips();
  }, []);

  const handleRecruitModalOpen = (open: boolean) => {
    setRecruitModalOpen(open);
  };

  const handleUserClick = async (each: any) => {
    if (!isAgileExec) {
      try {
        const acsUser = getAcsUserByCommunicationId(communityMembers, _.get(each, 'communicationId'));
        const coid = _.get(acsUser, 'id.coId', '');
        dispatch(taskActions.setUserId({ userId: coid }));
        dispatch(taskActions.setGroupId({ groupId: groupId }));
        const result = await API.getAllTasks({ grpId: groupId, userId: coid });
        const data = _.get(result.data, 'data', []);
        const levels = _.get(_.head(data), 'levels');
        const levelId = _.get(_.head(levels), '_id');
        const tasks = _.get(_.head(levels), 'tasks');
        const taskId = _.get(_.head(tasks), '_id');
        if (levelId && taskId) {
          const encodedName = encodeURIComponent(getDisplayName(each) || '');
          navigate(`/app/home/mentee/level/${levelId}/task/${taskId}?userName=${encodedName}`);
        }
      } catch (err) {
        toast.error(_.get(err, 'response.data.message', ''));
      }
    } else {
      navigateToChat(each?.communicationId, true, personalThreadsArr, navigate, dispatch);
    }
  };

  const handleProfileClick = async (each: any) => {
    const userId = each?.menteeId ?? each?.id ?? each?.userId ?? each?._id;
    const fallbackUser = {
      ...each,
      name: each?.displayName || each?.menteeName || each?.name || w('mentee'),
      id: userId ?? each?.id,
      _id: userId ?? each?._id
    };
    if (!userId || !groupId) {
      setProfileMentee(fallbackUser);
      setProfileModalOpen(true);
      return;
    }
    try {
      const response = await API.getUserProfileInAGroup(groupId, String(userId));
      if (response?.status === 200) {
        const userDetails = response?.data?.userDetails || response?.data?.data;
        const userForModal = {
          ...fallbackUser,
          ...userDetails,
          name:
            userDetails?.name ||
            userDetails?.displayName ||
            each?.displayName ||
            each?.menteeName ||
            each?.name ||
            w('mentee')
        };
        setProfileMentee(userForModal);
        setProfileModalOpen(true);
      } else {
        setProfileMentee(fallbackUser);
        setProfileModalOpen(true);
      }
    } catch (error) {
      setProfileMentee(fallbackUser);
      setProfileModalOpen(true);
    }
  };

  // const getBio = (mem: any) => {
  //   const { major = '', university = '' } = _.get(mem, 'bio.education', {});
  //   const { role = '', companyName = '' } = _.get(mem, 'bio.workHistory', {});
  //   return mem?.category === 'Student' ? bioFormatter(major, university) : bioFormatter(role, companyName);
  // };

  // const handleArticleClick = (article_id: any) => {
  //   navigate('/app/library', { state: { navigatedArticleId: article_id } });
  // };

  if (groupList.error) {
    return <Box>{groupList.errorText}</Box>;
  }

  if (groupList.data?.length === 0) {
    return <Box>No groups found</Box>;
  }
  // if (groupList.data) {
  //   const index = groupList.data?.findIndex(
  //     (each) => each.id === selectedGroupId.id && each.role === selectedGroupId.role
  //   );
  //   if (index && index === -1) {
  //     window.location.replace('/');
  //     return null;
  //   }
  // }

  // const profileDetails = useProfileDetails();
  // const userName = profileDetails.firstName + ' ' + profileDetails.lastName;

  const { type: activeModalType, mentee: activeModalMentee, objectiveId: activeModalObjectiveId } = activeMeetingModal;
  const meetingHistoryPast = meetingHistoryFromApi?.past ?? [];
  const meetingHistoryUpcoming = meetingHistoryFromApi?.upcoming ?? [];
  const meetingHistoryCanceled = meetingHistoryFromApi?.canceled ?? [];
  const filterByObjective = (list: any[]) => {
    if (!activeModalObjectiveId || !Array.isArray(list)) return list;
    return list.filter((m: any) => m?.objective != null && String(m.objective) === String(activeModalObjectiveId));
  };
  const pastFiltered = filterByObjective(meetingHistoryPast);
  const upcomingFiltered = filterByObjective(meetingHistoryUpcoming);
  const canceledFiltered = filterByObjective(meetingHistoryCanceled);
  const meetingsToShow =
    activeModalType === 'future'
      ? upcomingFiltered
      : activeModalType === 'history'
      ? [...pastFiltered, ...canceledFiltered]
      : [];
  /** Map meetingId -> enriched meeting from UserLearningObjectives for review/rating/feedback */
  const meetingDetailsByMeetingId = useMemo(() => {
    const map: Record<string, any> = {};
    (userLearningObjectives || []).forEach((obj: any) => {
      (obj.mentors || []).forEach((m: any) => {
        (m.meetings || []).forEach((meeting: any) => {
          const id = meeting?.meetingId ?? meeting?.callId ?? meeting?._id;
          if (id) map[String(id)] = { ...meeting, mentorInfo: m };
        });
      });
      (obj.mentees || []).forEach((mentee: any) => {
        (mentee.meetings || []).forEach((meeting: any) => {
          const id = meeting?.meetingId ?? meeting?.callId ?? meeting?._id;
          if (id && !map[String(id)]) map[String(id)] = { ...meeting, menteeInfo: mentee };
        });
      });
    });
    return map;
  }, [userLearningObjectives]);
  const activeModalObjectiveTitle =
    activeModalObjectiveId && displayObjectives?.length
      ? (displayObjectives.find(
          (o: any, idx: number) => getObjectiveId(o, idx) === activeModalObjectiveId
        ) as any)?.objective ?? activeModalMentee?.objective ?? 'Learning Objective'
      : activeModalMentee?.objective ?? 'Learning Objective';
  const menteeDisplayName = activeModalMentee ? getDisplayName(activeModalMentee) : w('mentee');
  const meetingModalTitle =
    activeModalType === 'future'
      ? `Future Meetings with ${menteeDisplayName}`
      : activeModalType === 'history'
      ? `Meeting History with ${menteeDisplayName}`
      : '';
  const meetingEmptyMessage =
    activeModalType === 'future'
      ? 'No upcoming meetings scheduled.'
      : activeModalType === 'history'
      ? 'No previous meetings logged yet.'
      : '';
  const objectiveTitle = activeModalObjectiveTitle;
  const getCallDateParts = (callRowData: any) => {
    if (!callRowData?.callDate || !callRowData?.callTime || !timezone) {
      return { month: '', day: '', weekday: '' };
    }
    const locale = 'en-US';
    const dateTime = timeZonWiseDateTime(
      callRowData.callDate.toString(),
      callRowData.callTime.toString(),
      timezone
    );
    return {
      month: dateTime.toLocaleString(locale, { month: 'short' }),
      day: dateTime.getDate().toString(),
      weekday: dateTime.toLocaleString(locale, { weekday: 'short' })
    };
  };
  const getMeetingTimeLabel = (callRowData: any) => {
    if (!callRowData?.callDate || !callRowData?.callTime || !timezone) return '';
    return `${timeZoneWiseTime(
      callRowData.callDate.toString(),
      callRowData.callTime.toString(),
      timezone
    ).toLowerCase()} (${timeZoneAbbreviation(timezone)})`;
  };

  if (loadingMentor) {
    return (
      <Box aria-busy="true" sx={{ padding: '25px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Bone height={28} width="40%" />
        <Bone height={160} radius="8px" />
        <Bone height={160} radius="8px" />
      </Box>
    );
  }

  const showCardsLoading = loading || !cardsStatsReady;

  return (
    <>
      <Box className="bodyBoxMentor" sx={{ padding: '25px', height: '100%' }}>
        {showCardsLoading ? (
          <Box
            sx={{
              width: '100%',
              minHeight: '60vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,
              background: '#151515'
            }}
          >
            <Box aria-busy="true" sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Bone height={28} width="40%" />
              <Bone height={160} radius="8px" />
              <Bone height={160} radius="8px" />
            </Box>
          </Box>
        ) : (
        <>
        <Box
          sx={{
            width: '100%',
            minHeight: '100%',
            // px: { xs: 2, md: 3 },
            // pt: { xs: 2, md: 3 },
            // pb: { xs: 10, md: 10 },
            padding: '25px',
            borderRadius: 2,
            background: '#151515',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            overflow: 'hidden'
          }}
        >
          <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 600 }}>{programName}</Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 2
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                gap: 0.5
              }}
            >
              {!isHybrid ? (
                <>
                  <Typography
                    sx={{
                      color: '#B6B6B6',
                      fontSize: 14,
                      fontWeight: 400
                    }}
                  >
                    {`Area of Expertise ${activeExpertiseIndex + 1}`}
                  </Typography>
                  <Typography
                    sx={{
                      color: '#F79420',
                      fontSize: 24,
                      fontWeight: 600,
                      textTransform: 'capitalize',
                      lineHeight: '32px'
                    }}
                  >
                    {activeObjective?.objective || `My ${w('mentee', { plural: true })} by Objective`}
                  </Typography>
                  <Typography
                    sx={{
                      color: '#868686',
                      fontSize: 20,
                      fontWeight: 600
                    }}
                  >
                    My {w('mentee', { plural: true })} for this Area of Expertise:
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    sx={{
                      color: '#B6B6B6',
                      fontSize: 14,
                      fontWeight: 400
                    }}
                  >
                    Matches
                  </Typography>
                  <Typography
                    sx={{
                      color: '#F79420',
                      fontSize: 26,
                      fontWeight: 600,
                      textTransform: 'none',
                      lineHeight: '31.20px'
                    }}
                  >
                    Program Matches
                  </Typography>
                  <Typography
                    sx={{
                      color: '#868686',
                      fontSize: 14,
                      fontWeight: 500
                    }}
                  >
                        This Guided Course doesn&apos;t use shared objectives. Each match is independent.
                  </Typography>
                </>
              )}
            </Box>
            <Box
              sx={{
                width: { xs: '100%', md: 384 },
                p: 1.75,
                background: '#0F0F0F',
                borderRadius: '5px',
                display: 'flex',
                alignItems: 'flex-start',
                flexDirection: 'column',
                gap: 1.5
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: 1,
                    background: '#1C1C1C',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      border: '2px solid #FDFDFD',
                      borderRadius: 0
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    color: '#FDFDFD',
                    fontSize: 10,
                    fontWeight: 700
                  }}
                >
                  Stay focused on creating impact
                </Typography>
              </Box>
              <Typography sx={{ color: '#FDFDFD', fontSize: 10 }}>{headerDescription}</Typography>
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            <Box
              sx={{
                flex: 1,
                pt: 2,
                pb: 4,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                gap: 2,
                flexWrap: 'wrap'
              }}
            >
              {isHybrid ? renderHybridMatchesList() : renderMentorshipMatchesList()}
              {/* {primaryMentee && (
                <Box
                  sx={{
                    width: 344,
                    height: 321,
                    p: 5,
                    borderRadius: 1.5,
                    background: 'var(--Fill-Card-card-highlight, #1C1C1C)',
                    border: '1px solid #2A2A2A',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    cursor: activeObjectiveId ? 'pointer' : 'not-allowed'
                  }}
                  onClick={() => {
                    if (!activeObjectiveId) return;
                    handleFindNewMentee(activeObjectiveId, expertiseTitle);
                  }}
                >
                  <Typography sx={{ fontSize: 22, fontWeight: 600, color: '#B6B6B6', textTransform: 'capitalize' }}>
                    Add {w('mentee')}
                  </Typography>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      background: '#1C1C1C',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <AddIcon sx={{ width: 24, height: 24, color: '#FDFDFD' }} />
                  </Box>
                </Box>
              )} */}
            </Box>
          </Box>
          {!isHybrid && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 24,
                left: '50%',
                transform: 'translateX(-50%)',
                minHeight: 64,
                background: '#2A2A2A',
                borderRadius: '16px',
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.55)',
                py: '12px',
                px: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  opacity: canGoPrev ? 1 : 0.5,
                  cursor: canGoPrev ? 'pointer' : 'not-allowed'
                }}
                onClick={canGoPrev ? handlePrevExpertise : undefined}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <ArrowBackIcon
                    sx={{
                      fontSize: 14,
                      color: canGoPrev ? '#FDFDFD' : '#444444'
                    }}
                  />
                </Box>
                <Typography sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 600 }}>Last Expertise</Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  opacity: canGoNext ? 1 : 0.5,
                  cursor: canGoNext ? 'pointer' : 'not-allowed'
                }}
                onClick={canGoNext ? handleNextExpertise : undefined}
              >
                <Typography sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 600 }}>Next Expertise</Typography>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <ArrowForwardIcon
                    sx={{
                      fontSize: 14,
                      color: canGoNext ? '#FDFDFD' : '#444444'
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}
          {!isHybrid && (
            <Box
              sx={{
                position: 'absolute',
                top: '40%',
                right: 0,
                transform: 'translateY(-50%)',
                height: 100,
                borderRadius: '4px 0 0 4px',
                border: '1px solid #2A2A2A',
                background: canGoNext ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 1,
                cursor: canGoNext ? 'pointer' : 'default'
              }}
              onClick={canGoNext ? handleNextExpertise : undefined}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  // borderRadius: '50%',
                  // border: `2px solid ${canGoNext ? '#FDFDFD' : '#444444'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <ArrowForwardIosOutlinedIcon
                  sx={{
                    color: canGoNext ? '#FDFDFD' : '#444444',
                    fontSize: 16
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>

      <Popover
        id={idMatchesTablePopover}
        open={openMatchesTablePopover}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <Box
          className="PopoverListStyle"
          onClick={() => {
            const matchId = getMatchId(selectedPopoverMentee);
            if (matchId) {
              handleOpenMatchCancel(matchId);
            }
            setAnchorEl(null);
          }}
        >
          <DeleteIcon />
          <Typography color="primary">Cancel match</Typography>
        </Box>
      </Popover>
      {/* Group of Peers Modal */}
      {isPeersPopupOpen && (
        <UserPeersPopup
          open={isPeersPopupOpen}
          onClose={() => handlePeersPopup(false)}
          peersList={peersList}
          onProfileClick={(mid) => moveToUsercommunityProfile(mid)}
          onChatClick={(each) => navigateMember(each)}
          onCalendarClick={(each) => handleCalendarOpen(each)}
          communityMembers={communityMembers} // Pass the communityMembers prop here
          navigate={navigate} // Pass the navigate prop here
        />
      )}

      {/* Request Match dialog */}
      <RequestMatch openRequestMatch={openRequestMatch} handleRequestMatch={handleRequestMatch} />

      {/* Request Match Reject dialog */}
      <RequestMatchReject
        openRequestMatchReject={openRequestMatchReject}
        handleRequestMatchReject={handleRequestMatchReject}
        requestId={requestId}
        groupId={groupId}
        mentorId={mentorId}
        menteeId={menteeId}
        doReload={doReload}
        commId={commId}
      />

      {/* Notes for Mallory Capoferri */}
      {/* <UserNotesPopup
        open={openUserNotes}
        handleOpen={handleOpenUserNotes}
      /> */}

      {/* Objective Modal */}
      <ObjectiveModal
        open={isObjectiveModalOpen}
        title={objective?.objective}
        onClose={handleCloseObjectiveModal}
        onSubmit={(data: { name: string; progress: number }) => {
          return Promise.resolve();
        }}
        description={objective?.description}
        status={objective?.status}
        objId={objective?._id}
        notes={objective?.notes}
        mentorsList={objective?.mentors?.filter((each: any) => each.userId === objUserId)}
        myMenteesList={selectedMentee}
        matchedObjectives={matchedObjectives}
      />

      {/* Cancel Match Modal */}
      <MatchCancelModal
        openMatchCancel={openMatchCancel}
        handleMatchCancel={handleCloseMatchCancel} // Close modal on cancel
        confirmMatchId={selectedMatchId}
        refetch={refetch}
      />

      {/* Match Cancel Confirmation Modal */}
      {/* <ConfirmationModal
        open={openMatchCancel}
        onClose={handleCloseMatchCancel}
        onConfirm={() => {
          handleCloseMatchCancel();
        }}
        message="Are you sure you want to cancel?"
        messageStyle={{ py: 2 }}
        confirmText="Yes"
      /> */}

      {/* Calendar Modal */}
      <CallBookingModal
        title={objective?.objective}
        handleCloseModal={handleCalendarClose}
        openFlag={openCalendarModal}
        chatProfileName={displayName}
        threadParticipantsUsers={participantUser}
        chatType="Chats"
        pageName="Mentor"
        bookedCallDetails={bookedCallDetails}
        onMeetingScheduled={handleMeetingScheduled}
      />

      {recruitModalOpen && (
        <RecruitNewMentor
          title=""
          open={recruitModalOpen}
          onClose={() => setRecruitModalOpen(false)}
          mentorsList={recruitMentors}
          onProfileClick={(mid: any) => moveToUsercommunityProfile(mid)}
          onChatClick={(each: any) => navigateMember(each)}
          onCalendarClick={(each: any) => handleCalendarOpen(each)}
          objId=""
          grpId={groupId}
          apiType="mentee"
          learningObjective={activeObjective}
        />
      )}

      {/* Task Modal */}
      {/* Welcome Modals */}
      {/* <WelcomeModals open={openGettingStartedModal} handleClickWelcomeModals={handleClickWelcomeModals} /> */}

      {/* User Profile Details Popup */}
      <UserProfileDetailsPopup
        loading={false} // loader
        openUserProfileDetails={openUserProfileDetails}
        handleopenUserProfileDetails={handleopenUserProfileDetails}
        userData={selectedMenteeInfo}
      />

      <Modal open={Boolean(activeMeetingModal.type)} onClose={handleCloseMeetingModal}>
        {activeModalType === 'future' ? (
          <Box
            sx={{
              width: 550,
              maxWidth: '90vw',
              maxHeight: '90vh',
              bgcolor: '#0F0F0F',
              borderRadius: '8px',
              outline: '1px solid #444444',
              outlineOffset: '-1px',
              overflow: 'hidden',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box
              sx={{
                alignSelf: 'stretch',
                pl: 3,
                pr: '44px',
                py: 2.5,
                borderBottom: '1px solid #2A2A2A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
                {meetingModalTitle || 'Future Meetings'}
              </Typography>
              <IconButton size="small" onClick={handleCloseMeetingModal} sx={{ color: '#FDFDFD', position: 'absolute', right: 12, top: 20 }}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ alignSelf: 'stretch', p: 3, display: 'flex', flexDirection: 'column', gap: 2.5, overflowY: 'auto', maxHeight: 'calc(90vh - 100px)' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', mb: 1 }}>
                      <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 400 }}>
                        Learning objective
                      </Typography>
                <Typography sx={{ color: '#F79420', fontSize: 16, fontWeight: 700 }}>
                        {objectiveTitle || ''}
                </Typography>
              </Box>

              {meetingHistoryLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                  <CircularProgress size={32} sx={{ color: '#F79420' }} />
                </Box>
              ) : meetingsToShow.length ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {meetingsToShow.map((meeting: any, index: number) => {
                    const { month, day, weekday } = getCallDateParts(meeting);
                    const meetingTimeLabel = getMeetingTimeLabel(meeting);
                    const durationLabel =
                      typeof meeting?.callTimeInterval === 'number'
                        ? `${meeting.callTimeInterval}'`
                        : '';
                    const meetingLink = meeting?.meetingLink || meeting?.meeting_link;
                    return (
                      <Box
                        key={meeting?.meetingId || meeting?.callId || index}
                        sx={{
                          p: 1.75,
                          bgcolor: '#1C1C1C',
                          borderRadius: '5px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.75
                        }}
                      >
                        <Box
                          sx={{
                            width: 56,
                            flexShrink: 0,
                            borderRadius: '5px',
                            border: '1px solid #2A2A2A',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                          }}
                        >
                          <Box sx={{ width: '100%', px: 1.25, py: 0.5, bgcolor: '#3B82F6', textAlign: 'center' }}>
                            <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 500 }}>
                                    {month || ''}
                            </Typography>
                          </Box>
                          <Box sx={{ py: 0.5, textAlign: 'center' }}>
                                  <Typography sx={{ color: '#fff', fontSize: 24, fontWeight: 700 }}>
                                    {day || ''}
                                  </Typography>
                            <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 500 }}>{weekday || ''}</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                          <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700 }} noWrap>
                            {meeting?.callTitle || `Meeting ${index + 1}`}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                            {meetingTimeLabel && (
                              <Typography sx={{ color: '#868686', fontSize: 14, fontWeight: 500 }}>
                                {meetingTimeLabel}
                              </Typography>
                            )}
                            {durationLabel && (
                              <>
                                <Box sx={{ width: '1px', alignSelf: 'stretch', minHeight: 14, bgcolor: '#404040' }} />
                                <Typography sx={{ color: '#868686', fontSize: 14, fontWeight: 500 }}>
                                  {durationLabel}
                                </Typography>
                              </>
                            )}
                            <Box sx={{ width: '1px', alignSelf: 'stretch', minHeight: 14, bgcolor: '#404040' }} />
                            <Typography sx={{ color: '#868686', fontSize: 14, fontWeight: 500 }}>
                              {menteeDisplayName}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
                          <Button
                            size="small"
                            sx={{
                              px: 1.25,
                              py: 0.5,
                              fontSize: 12,
                              fontWeight: 500,
                              textTransform: 'none',
                              color: '#FDFDFD',
                              border: '1px solid #2A2A2A',
                              bgcolor: '#1C1C1C',
                              '&:hover': { bgcolor: '#2A2A2A' }
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            onClick={() =>
                              openMeetingWithRedirect(meetingLink, {
                                meetingId: meeting?.meetingId,
                                meetingType: meeting?.meetingType,
                                callDate: meeting?.callDate,
                                callTime: meeting?.callTime,
                                callTimeInterval: meeting?.callTimeInterval
                              })
                            }
                            sx={{
                              px: 1.25,
                              py: 0.5,
                              fontSize: 12,
                              fontWeight: 600,
                              textTransform: 'none',
                              background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
                              color: '#fff',
                              '&:hover': { background: 'linear-gradient(180deg, #E0851a 0%, #d94e20 100%)' }
                            }}
                          >
                            Join
                          </Button>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography sx={{ color: '#B6B6B6', fontSize: 14, textAlign: 'center' }}>
                  {meetingEmptyMessage || 'No upcoming meetings scheduled.'}
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              width: 550,
              maxWidth: '90vw',
              maxHeight: '90vh',
              bgcolor: '#0F0F0F',
              borderRadius: '8px',
              outline: '1px solid #444444',
              outlineOffset: '-1px',
              overflow: 'hidden',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box
              sx={{
                alignSelf: 'stretch',
                pl: 3,
                pr: '44px',
                py: 2.5,
                borderBottom: '1px solid #2A2A2A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
                {meetingModalTitle || 'Meeting History'}
              </Typography>
              <IconButton size="small" onClick={handleCloseMeetingModal} sx={{ color: '#FDFDFD', position: 'absolute', right: 12, top: 20 }}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ alignSelf: 'stretch', p: 3, display: 'flex', flexDirection: 'column', gap: 2.5, overflowY: 'auto', maxHeight: 'calc(90vh - 100px)' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 400, fontFamily: 'Inter, sans-serif' }}>
                        Learning objective
                </Typography>
                <Typography sx={{ color: '#F79420', fontSize: 16, fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
                        {objectiveTitle || ''}
                </Typography>
              </Box>

              {meetingHistoryLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                  <CircularProgress size={32} sx={{ color: '#F79420' }} />
                </Box>
              ) : meetingsToShow.length ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {meetingsToShow.map((meeting: any, index: number) => {
                    const isPast = pastFiltered.some(
                      (p: any) => p?.meetingId === meeting?.meetingId || p?.callId === meeting?.callId
                    );
                    const isCanceled = canceledFiltered.some(
                      (c: any) => c?.meetingId === meeting?.meetingId || c?.callId === meeting?.callId
                    );
                    const meetingIdKey = meeting?.meetingId ?? meeting?.callId ?? meeting?._id;
                    const details = meetingIdKey ? meetingDetailsByMeetingId[String(meetingIdKey)] : null;
                    const callReview = details?.callReview ?? meeting?.callReview;
                    const menteeReviewData = meeting?.menteeReview ?? details?.menteeRating ?? meeting?.menteeRating;
                    const mentorReviewData = meeting?.mentorReview ?? details?.mentorRating ?? meeting?.mentorRating;
                    const callDetails = details?.callDetails ?? meeting?.callDetails ?? meeting;
                    const menteeReviewText =
                      menteeReviewData?.review ?? (typeof menteeReviewData === 'string' ? menteeReviewData : null) ?? menteeReviewData?.feedback ?? '';
                    const menteeReviewRating = Number(menteeReviewData?.rating ?? menteeReviewData?.vote ?? 0);
                    const mentorReviewText =
                      mentorReviewData?.review ?? (typeof mentorReviewData === 'string' ? mentorReviewData : null) ?? callReview?.review ?? meeting?.review ?? '';
                    const mentorReviewRating = Number(mentorReviewData?.rating ?? mentorReviewData?.vote ?? callReview?.rating ?? 0);
                    const menteeInfo = details?.menteeInfo ?? activeModalMentee;
                    const menteeName = menteeInfo?.name ?? menteeInfo?.displayName ?? menteeDisplayName;
                    const menteeHeadshot = menteeInfo?.image ?? menteeInfo?.headshot ?? activeModalMentee?.image ?? activeModalMentee?.headshot;
                    const feedbackDate = menteeReviewData?.date ?? menteeReviewData?.createdAt ?? callDetails?.callDate;
                    const { month, day, weekday } = getCallDateParts(meeting);
                    const meetingTimeLabel = getMeetingTimeLabel(meeting);
                    const durationLabel =
                      typeof meeting?.callTimeInterval === 'number'
                        ? `${meeting.callTimeInterval} Min`
                        : '';
                    const statusLabel = isCanceled ? 'Canceled' : 'Completed';
                    const statusColor = isCanceled ? '#868686' : '#00972D';
                    const dateFormatted =
                      month && day
                        ? `${month}/${day}`
                        : feedbackDate
                        ? (typeof feedbackDate === 'string' ? new Date(feedbackDate) : new Date(feedbackDate)).toLocaleDateString('en-US', {
                            month: 'numeric',
                            day: 'numeric',
                            year: '2-digit'
                          })
                              : '';
                    return (
                      <Box
                        key={meeting?.meetingId || meeting?.callId || meeting?._id || index}
                        sx={{
                          p: 1.75,
                          borderRadius: '5px',
                          border: '1px solid #444444',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.25
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700 }}>
                              {meeting?.callTitle ?? callDetails?.callTitle ?? `Meeting ${index + 1}`}
                            </Typography>
                            <Box
                              sx={{
                                px: 1.5,
                                py: 0.375,
                                borderRadius: '20px',
                                bgcolor: statusColor,
                                color: '#fff',
                                fontSize: 12,
                                fontWeight: 400
                              }}
                            >
                              {statusLabel}
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <AccessTimeIcon sx={{ fontSize: 16, color: '#B6B6B6' }} />
                              <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>
                                      {month && day ? `${month}/${day}` : meeting?.callDate || ''}
                              </Typography>
                            </Box>
                            {meetingTimeLabel && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <AccessTimeIcon sx={{ fontSize: 16, color: '#B6B6B6' }} />
                                <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>{meetingTimeLabel}</Typography>
                              </Box>
                            )}
                            {durationLabel && (
                              <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>{durationLabel}</Typography>
                            )}
                          </Box>
                        </Box>
                        {isPast && !isCanceled && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>
                                {w('mentee')} Review:
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                                <ChatAvatarComponent
                                  image={menteeHeadshot}
                                  firstLetter={menteeName?.charAt(0) || ''}
                                  width="40px"
                                  height="40px"
                                  type="noStatus"
                                />
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>
                                      {menteeName}
                                    </Typography>
                                    <Typography sx={{ color: '#B6B6B6', fontSize: 10, fontWeight: 500 }}>
                                      {dateFormatted}
                                    </Typography>
                                  </Box>
                                  {menteeReviewText ? (
                                    <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 400 }}>
                                      {menteeReviewText}
                                    </Typography>
                                  ) : (
                                    <Typography sx={{ color: '#868686', fontSize: 14, fontStyle: 'italic' }}>No review yet</Typography>
                                  )}
                                  {menteeReviewRating > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                      {[1, 2, 3, 4, 5].map((star) => (
                                        <StarIcon
                                          key={star}
                                          sx={{
                                            fontSize: 14,
                                            color: star <= menteeReviewRating ? '#FDFDFD' : '#444444'
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>
                                {w('mentor')} Review:
                              </Typography>
                              {mentorReviewText ? (
                                <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 500 }}>
                                  {mentorReviewText}
                                </Typography>
                              ) : (
                                <Typography sx={{ color: '#868686', fontSize: 14, fontStyle: 'italic' }}>No review yet</Typography>
                              )}
                              {mentorReviewRating > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>Rated:</Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <StarIcon
                                        key={star}
                                        sx={{
                                          fontSize: 16,
                                          color: star <= mentorReviewRating ? '#FDFDFD' : '#444444'
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography sx={{ color: '#B6B6B6', fontSize: 14, textAlign: 'center' }}>
                  {meetingEmptyMessage || 'No previous meetings logged yet.'}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Modal>
        </>
        )}
      </Box>
      <MenteeProfileModal
        open={profileModalOpen}
        onClose={() => {
          setProfileModalOpen(false);
          setProfileMentee(null);
        }}
        user={profileMentee || {}}
      />
    </>
  );
};

export default Mentor;
